import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  ShoppingCart,
  Zap,
  Activity,
  Sun,
  Moon,
  Landmark,
  Shield,
  Scale,
  Building2,
  Languages,
  HelpCircle,
  BookOpen,
  PawPrint,
  ExternalLink,
  FileText,
  FileSignature,
  BarChart3,
  Newspaper,
  Map as MapIcon,
  Radio,
  Fuel
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getLayerSlug } from '../../utils/routeUtils';
import { useMapStore } from '../../store/useMapStore';
import { useTranslation } from '../../i18n/translations';
import type { ServiceLayer } from '../../types/gis';
import { APP_VERSION } from '../../constants';
import { formatVersion } from '../../utils/version';
import { useVisitorCount } from '../../hooks/useVisitorCount';
import { SHOW_ELECTION_RESULTS, ENABLE_LAND_PARCEL } from '../../config/features';

const Sidebar: React.FC = () => {
  const isSidebarOpen = useMapStore(state => state.isSidebarOpen);
  const setSidebarOpen = useMapStore(state => state.setSidebarOpen);
  const activeLayer = useMapStore(state => state.activeLayer);
  const activeDistrict = useMapStore(state => state.activeDistrict);
  const theme = useMapStore(state => state.theme);
  const toggleTheme = useMapStore(state => state.toggleTheme);
  const language = useMapStore(state => state.language);
  const setLanguage = useMapStore(state => state.setLanguage);
  const constituencyType = useMapStore(state => state.constituencyType);
  const setConstituencyType = useMapStore(state => state.setConstituencyType);
  const setLegalModal = useMapStore(state => state.setLegalModal);
  const setIsTutorialOpen = useMapStore(state => state.setIsTutorialOpen);

  const { t } = useTranslation();
  const { total, today } = useVisitorCount();

  const categories = [
    {
      label: t('ESSENTIALS'),
      items: [
        { id: 'PINCODE' as ServiceLayer, label: t('PINCODE'), icon: MapPin, color: '#3b82f6' },
        { id: 'TNEB' as ServiceLayer, label: t('TNEB'), icon: Zap, color: '#f59e0b' },
        { id: 'PDS' as ServiceLayer, label: t('PDS'), icon: ShoppingCart, color: '#ef4444' },
        { id: 'FUEL' as ServiceLayer, label: 'Fuel Outlets', icon: Fuel, color: '#34d399' },
      ]
    },
    {
      label: language === 'ta' ? 'சுகாதாரம் & பாதுகாப்பு' : 'HEALTH & SAFETY',
      items: [
        { id: 'HEALTH' as ServiceLayer, label: t('HEALTH'), icon: Activity, color: '#be123c' },
        { id: 'VETERINARY' as ServiceLayer, label: t('VETERINARY'), icon: PawPrint, color: '#10b981' },
        { id: 'POLICE' as ServiceLayer, label: t('POLICE'), icon: Shield, color: '#475569' },
      ]
    },
    {
      label: t('CIVIC'),
      items: [
        { id: 'LAND_PARCEL' as ServiceLayer, label: t('LAND_PARCEL'), icon: MapIcon, color: '#d97706', isBeta: true },
        { id: 'REGISTRATION' as ServiceLayer, label: t('REGISTRATION'), icon: FileSignature, color: '#6366f1' },
        { id: 'LOCAL_BODIES_V2' as ServiceLayer, label: t('LOCAL_BODIES_V2'), icon: Building2, color: '#6366f1' },
        { id: 'CONSTITUENCY' as ServiceLayer, label: t('CONSTITUENCY'), icon: Landmark, color: '#6366f1' },
      ].filter(item => item.id !== 'LAND_PARCEL' || ENABLE_LAND_PARCEL)
    },
    {
      label: t('CULTURE_AND_HERITAGE'),
      items: [
        { id: 'TEMPLES' as ServiceLayer, label: t('TEMPLES'), icon: Landmark, color: '#a21caf' },
        { id: 'RADIO' as ServiceLayer, label: 'Akashvani FM', icon: Radio, color: '#ec4899', isBeta: true },
      ]
    }
  ];

  return (
    <>
      {/* Backdrop for Mobile */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-backdrop"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.aside
        role="navigation"
        aria-label={t('SERVICE_LAYERS_ARIA') || "Service Layers"}
        initial={false}
        animate={{ 
          width: isSidebarOpen ? 280 : 0,
          borderRightWidth: isSidebarOpen ? 1 : 0
        }}
        transition={{ duration: 0.2 }}
        className={`sidebar ${!isSidebarOpen ? 'sidebar-closed' : ''}`}
        aria-hidden={!isSidebarOpen}
        style={{
          borderRightStyle: 'solid',
          borderRightColor: 'var(--border-glass)',
          boxShadow: !isSidebarOpen ? 'none' : undefined
        }}
      >
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          aria-label={isSidebarOpen ? t('CLOSE_SIDEBAR') : t('OPEN_SIDEBAR')}
        >
          {isSidebarOpen ? <ChevronLeft size={20} aria-hidden="true" focusable="false" /> : <ChevronRight size={20} aria-hidden="true" focusable="false" />}
        </button>

        <motion.nav
          className="sidebar-scroll-content"
          aria-label={t('SERVICE_LAYERS_ARIA') || "Service Layers"}
          initial={false}
          animate={{
            opacity: isSidebarOpen ? 1 : 0,
            pointerEvents: isSidebarOpen ? 'auto' : 'none'
          }}
          transition={{ duration: 0.2 }}
          style={{ 
            display: isSidebarOpen ? 'flex' : 'none' 
          }}
        >
          {isSidebarOpen && (
            <div className="sidebar-mascot-container">
              <img 
                src="/branding/mascot.webp" 
                alt="N-Bot" 
                className="mascot-img" 
                loading="eager"
                fetchPriority="high"
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
          <div className="sidebar-header">
            <div className="sidebar-logo-group">
              <img
                src="/branding/icon.png"
                alt="NammaMap Logo"
                loading="eager"
                fetchPriority="high"
                style={{ width: 32, height: 32, objectFit: 'contain' }}
              />
              <motion.h1
                animate={{ opacity: isSidebarOpen ? 1 : 0 }}
                className="logo-text"
              >
                NammaMap
              </motion.h1>
            </div>
            {isSidebarOpen && <h2 className="sub-logo-text">{t('INDEPENDENT_PORTAL')}</h2>}
          </div>

          {categories.map((category) => (
          <div key={category.label} className="sidebar-section">
            <h3 className="sidebar-section-label">{category.label}</h3>
            <ul className="sidebar-menu-list">
              {category.items.map((item) => (
                <li key={`sidebar-item-${item.id}`}>
                  <Link
                    to={`/${getLayerSlug(item.id)}${activeDistrict ? `/${encodeURIComponent(activeDistrict)}` : ''}`}
                    className={`sidebar-menu-item ${activeLayer === item.id ? 'active' : ''}`}
                    aria-current={activeLayer === item.id ? 'page' : undefined}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon size={20} style={{ color: activeLayer === item.id ? 'white' : item.color }} aria-hidden="true" focusable="false" />
                    <AnimatePresence>
                      {isSidebarOpen && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          {item.label}
                          {item.isBeta && (
                            <span className="badge-beta">
                              {t('EXPERIMENTAL')}
                            </span>
                          )}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Link>

                  {/* Sub-tabs for Constituencies - Inline after the menu item */}
                  {item.id === 'CONSTITUENCY' && (
                    <AnimatePresence>
                      {activeLayer === 'CONSTITUENCY' && isSidebarOpen && SHOW_ELECTION_RESULTS && (
                        <motion.div 
                          key="constituency-sub-tabs"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="sidebar-sub-tabs"
                          style={{ 
                            padding: '0 12px 12px 12px',
                            position: 'relative',
                            zIndex: 10
                          }}
                        >
                          <div className="tab-group" role="tablist" style={{ 
                            display: 'flex', 
                            background: theme === 'dark' ? '#1e293b' : '#f1f5f9',
                            borderRadius: '8px',
                            padding: '4px',
                            gap: '4px',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                          }}>
                            <button 
                              key="ac-btn"
                              role="tab"
                              aria-selected={constituencyType === 'AC'}
                              onClick={() => {
                                setConstituencyType('AC');
                                setSidebarOpen(false);
                              }}
                              style={{
                                flex: 1,
                                padding: '8px 0',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                background: constituencyType === 'AC' ? (theme === 'dark' ? '#334155' : '#fff') : 'transparent',
                                color: constituencyType === 'AC' ? (theme === 'dark' ? '#f8fafc' : '#1e293b') : (theme === 'dark' ? '#94a3b8' : '#64748b'),
                                boxShadow: constituencyType === 'AC' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s'
                              }}
                            >
                              {t('ASSEMBLY')}
                            </button>
                            <button 
                              key="pc-btn"
                              role="tab"
                              aria-selected={constituencyType === 'PC'}
                              onClick={() => {
                                setConstituencyType('PC');
                                setSidebarOpen(false);
                              }}
                              style={{
                                flex: 1,
                                padding: '8px 0',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                background: constituencyType === 'PC' ? (theme === 'dark' ? '#334155' : '#fff') : 'transparent',
                                color: constituencyType === 'PC' ? (theme === 'dark' ? '#f8fafc' : '#1e293b') : (theme === 'dark' ? '#94a3b8' : '#64748b'),
                                boxShadow: constituencyType === 'PC' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s'
                              }}
                            >
                              {t('PARLIAMENT')}
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}



        {/* NammaArasu External Links - Modular Section */}
        <div className="sidebar-section">
          <h3 className="sidebar-section-label" style={{ marginBottom: '4px' }}>{t('NAMMA_ARASU')}</h3>
          <span className="badge-beta" style={{ marginLeft: '16px', marginBottom: '12px', display: 'inline-block' }}>
            {t('EXPERIMENTAL_TAG')}
          </span>
          <ul className="sidebar-menu-list">
            <li>
              <a 
                href="https://manifesto.nammamap.in/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="sidebar-menu-item"
              >
                <FileText size={20} style={{ color: '#ec4899' }} aria-hidden="true" focusable="false" />
                <AnimatePresence>
                  {isSidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      {t('MANIFESTO')}
                      <ExternalLink size={12} style={{ opacity: 0.5 }} aria-hidden="true" focusable="false" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </a>
            </li>

            <li>
              <a 
                href="https://tracker.nammamap.in/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="sidebar-menu-item"
              >
                <BarChart3 size={20} style={{ color: '#8b5cf6' }} aria-hidden="true" focusable="false" />
                <AnimatePresence>
                  {isSidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      {t('TRACKER')}
                      <ExternalLink size={12} style={{ opacity: 0.5 }} aria-hidden="true" focusable="false" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </a>
            </li>

            <li>
              <a 
                href="https://news.nammamap.in/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="sidebar-menu-item"
              >
                <Newspaper size={20} style={{ color: '#06b6d4' }} aria-hidden="true" focusable="false" />
                <AnimatePresence>
                  {isSidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      {t('NEWS')}
                      <ExternalLink size={12} style={{ opacity: 0.5 }} aria-hidden="true" focusable="false" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </a>
            </li>
          </ul>
        </div>

        <div className="sidebar-divider" aria-hidden="true" />
        <h3 className="sidebar-section-label sidebar-settings-label">{t('SETTINGS')}</h3>
        
        <div className="sidebar-footer sidebar-footer-compact">
          <div className="sidebar-utility-panel">
            <div className="sidebar-utility-row">
              <button
                className="sidebar-utility-item"
                onClick={() => setLanguage(language === 'en' ? 'ta' : 'en')}
                aria-label={language === 'en' ? t('SWITCH_TO_TAMIL') : t('SWITCH_TO_ENGLISH')}
              >
                <Languages size={16} aria-hidden="true" focusable="false" />
                {isSidebarOpen && <span>{language === 'en' ? 'தமிழ்' : 'English'}</span>}
              </button>
              <button
                className="sidebar-utility-item"
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? t('SWITCH_TO_LIGHT_MODE') : t('SWITCH_TO_DARK_MODE')}
              >
                {theme === 'dark' ? <Sun size={16} aria-hidden="true" focusable="false" /> : <Moon size={16} aria-hidden="true" focusable="false" />}
                {isSidebarOpen && <span>{theme === 'dark' ? t('LIGHT_MODE') : t('DARK_MODE')}</span>}
              </button>
            </div>

            <div className="sidebar-utility-row">
              <div className="sidebar-utility-links">
                <button
                  className="sidebar-utility-link"
                  onClick={() => setLegalModal(true, 'about')}
                  aria-label={t('ABOUT_LEGAL')}
                >
                  <Scale size={13} aria-hidden="true" focusable="false" />
                  {isSidebarOpen && <span>About</span>}
                </button>
                <button
                  className="sidebar-utility-link"
                  onClick={() => setIsTutorialOpen(true)}
                  aria-label={t('TUTORIAL')}
                >
                  <HelpCircle size={13} aria-hidden="true" focusable="false" />
                  {isSidebarOpen && <span>{t('TUTORIAL')}</span>}
                </button>
                <Link to="/guides" className="sidebar-utility-link" aria-label="Module Guides">
                  <BookOpen size={13} aria-hidden="true" focusable="false" />
                  {isSidebarOpen && <span>Guides</span>}
                </Link>
              </div>
            </div>
          </div>

          {isSidebarOpen && (
            <div className="sidebar-version-tag">
              <div>v{formatVersion(APP_VERSION)}</div>
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.8, fontSize: '11px' }}
                >
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} aria-hidden="true" />
                  <span>{t('TOTAL_VISITS')}: <strong style={{ color: 'var(--text-primary)' }}>{(total || 0).toLocaleString()}</strong></span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ opacity: 0.6, fontSize: '10px', fontWeight: 500 }}
                >
                  {t('TODAY_VISITS')}: {(today || 0).toLocaleString()}
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </motion.nav>
    </motion.aside>
    </>
  );
};

export default Sidebar;
